import { FC } from "react";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { SATOSLink } from "@/app/application/SATOSLink";
import { Translate } from "@/app/application/Translate";
import { useTrainingBack } from "@/features/training/hooks/useTrainingBack";
import type { TrainingFile } from "@/features/training/model/Training/TrainingFile";
import { KpiParameters } from "@/features/training/pages/drill-tab/panel-right/drill/KpiParameters";
import { SessionsList } from "@/features/training/pages/drill-tab/panel-right/drill/SessionsList";
import { TrainingDrillActions } from "@/features/training/pages/drill-tab/panel-right/drill/TrainingDrillActions";
import { DrillDetailSkeleton } from "@/features/training/pages/training-tab/CoursesSkeletons";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import {
	Box,
	Chip,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	Paper,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import {
	useFetchActiveSessionsByDrillId,
	useFetchArchivedSessionsByDrillId,
	useFetchDrill,
} from "@/service/TrainingQueries";

/* ---------- helpers ---------- */

const getFileIcon = (filename: string) => {
	const ext = filename.split(".").pop()?.toLowerCase();
	if (["pdf"].includes(ext || "")) return <PictureAsPdfIcon fontSize="small" />;
	if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext || ""))
		return <ImageIcon fontSize="small" />;
	return <DescriptionIcon fontSize="small" />;
};

/* ---------- component ---------- */

interface TrainingDrillProps {
	id: number;
}

export const TrainingDrill: FC<TrainingDrillProps> = ({ id }) => {
	const theme = useTheme();
	const handleBack = useTrainingBack();
	const { data: drill, isLoading: isLoadingDrill } = useFetchDrill(id);

	if (isLoadingDrill || !drill) {
		return (
			<Box
				sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
			>
				<DrillDetailSkeleton />
			</Box>
		);
	}

	const attachments = Array.isArray(drill.Attachments) ? drill.Attachments : [];

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Compact header */}
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1.5,
					flexWrap: "wrap",
					py: 1.15,
					px: { xs: 2, sm: 2.5 },
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Tooltip
					title={<Translate String="Common.Back">Back</Translate>}
					enterDelay={400}
				>
					<IconButton
						onClick={handleBack}
						size="small"
						sx={{
							color: "primary.main",
							"&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
						}}
					>
						<ArrowBackIosIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>

				<Box sx={{ flex: 1, minWidth: 280 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							mb: 0.05,
							color: "primary.main",
							fontWeight: 700,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						<Translate String="Auto.Features.Training.Pages.DrillTab.PanelRight.Drill.TrainingDrill.L124C7.Drill">Drill</Translate>
					</Typography>
					<Typography variant="subtitle1" fontWeight={700} noWrap title={drill.Name}>
						{drill.Name}
					</Typography>
					<Stack
						direction="row"
						spacing={0.75}
						useFlexGap
						flexWrap="wrap"
						sx={{
							mt: 0.5,
						}}
					>
						<Chip
							size="small"
							label={drill.Trainer?.Name || "—"}
							sx={{
								height: 24,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.primary.main, 0.08),
								fontWeight: 700,
							}}
						/>
						<Chip
							size="small"
							label={String(drill.Type || "—")}
							sx={{
								height: 24,
								borderRadius: 2,
								bgcolor: alpha(theme.palette.info.main, 0.08),
								fontWeight: 700,
							}}
						/>
						{attachments.length > 0 && (
							<Chip
								size="small"
								label={`${attachments.length} attachment${attachments.length === 1 ? "" : "s"}`}
								sx={{
									height: 24,
									borderRadius: 2,
									bgcolor: alpha(theme.palette.warning.main, 0.08),
									fontWeight: 700,
								}}
							/>
						)}
					</Stack>
				</Box>

				<TrainingDrillActions drillId={id} type={drill.Type} />
			</Box>

			{/* Scrollable content: Overview + Sessions | KPI (pattern like CourseSession) */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
				}}
			>
				{/* Overview: full width at top */}
				<Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
					<Paper
						variant="outlined"
						sx={{
							p: 2,
							borderRadius: 3,
							borderColor: "divider",
						}}
					>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								mb: 0.1,
								color: "primary.main",
								fontWeight: 700,
								letterSpacing: "0.08em",
								textTransform: "uppercase",
							}}
						>
							<Translate String="Training.Drill.Overview">Overview</Translate>
						</Typography>

						{drill.Description ? (
							<Box sx={{ mt: 1.5 }}>
								<SATOSContentRender
									isNotProcedureRender={true}
									content={drill.Description}
								/>
							</Box>
						) : (
							<Typography
								variant="body2"
								color="text.secondary"
								fontStyle="italic"
								sx={{ mt: 1 }}
							>
								<Translate String="Common.NoDescription">
									No description provided.
								</Translate>
							</Typography>
						)}

						<Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 3 }}>
							<Box>
								<Typography
									variant="caption"
									color="text.secondary"
									display="block"
								>
									<Translate String="Training.Drill.Responsible">
										Responsible
									</Translate>
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{drill.Trainer?.Name || <em>—</em>}
								</Typography>
							</Box>
							<Box>
								<Typography
									variant="caption"
									color="text.secondary"
									display="block"
								>
									<Translate String="Training.Drill.Type">Type</Translate>
								</Typography>
								<Typography variant="body2" fontWeight={500}>
									{drill.Type || <em>—</em>}
								</Typography>
							</Box>
						</Box>

						{attachments.length > 0 && (
							<Box sx={{ mt: 2 }}>
								<Typography
									variant="caption"
									color="text.secondary"
									display="block"
									sx={{ mb: 1 }}
								>
									<Translate String="Training.Drill.Attachments">
										Attachments
									</Translate>
								</Typography>
								<List dense disablePadding>
									{attachments.map(
										(x: TrainingFile) =>
											x?.UrlPath &&
											x?.Filename && (
												<Box
													key={x.Id ?? x.Filename}
													sx={{ "& a": { textDecoration: "none" } }}
												>
													<SATOSLink to={x.UrlPath} target="_blank">
														<ListItem
															sx={{
																px: 0,
																py: 0.75,
																borderRadius: 2,
																"&:hover": {
																	bgcolor: alpha(
																		theme.palette.primary.main,
																		0.06,
																	),
																},
															}}
														>
															<ListItemIcon
																sx={{ minWidth: 32, color: "primary.main" }}
															>
																{getFileIcon(x.Filename)}
															</ListItemIcon>
															<Typography
																variant="body2"
																fontWeight={500}
																noWrap
																sx={{ flex: 1 }}
															>
																{x.Filename}
															</Typography>
															<OpenInNewIcon
																sx={{ fontSize: 14, color: "text.disabled" }}
															/>
														</ListItem>
													</SATOSLink>
												</Box>
											),
									)}
								</List>
							</Box>
						)}
					</Paper>
				</Box>

				{/* Two columns: Sessions (left) | Configuration (right) */}
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
					}}
				>
					{/* Left: Sessions */}
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							borderRight: { md: 1 },
							borderBottom: { xs: 1, md: 0 },
							borderColor: "divider",
							px: 2,
							py: 1.5,
						}}
					>
						<SessionsList
							entityId={drill.Id}
							entityName={drill.Name}
							fetchActiveSessions={useFetchActiveSessionsByDrillId}
							fetchArchivedSessions={useFetchArchivedSessionsByDrillId}
							sectionTitle="Training.Session.Sessions"
							activeTitle="Training.Drill.Sessions.ActiveTitle"
							archivedTitle="Training.Drill.Sessions.ArchivedTitle"
							basePath={`/training/drills/${drill.Id}/sessions`}
							compact
						/>
					</Box>

					{/* Right: Configuration (KPI) */}
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							px: 2,
							py: 1.5,
						}}
					>
						{drill?.KpiParameters?.length ? (
							<KpiParameters kpis={drill.KpiParameters} compact />
						) : (
							<Paper
								variant="outlined"
								sx={{
									p: 3,
									borderRadius: 3,
									textAlign: "center",
									borderColor: "divider",
									borderStyle: "dashed",
								}}
							>
								<SpeedRoundedIcon
									sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }}
								/>
								<Typography
									variant="body1"
									color="text.secondary"
									fontWeight={500}
								>
									<Translate String="Training.Drill.KpiParameters.NoParameters">
										No KPI parameters configured.
									</Translate>
								</Typography>
								<Typography
									variant="body2"
									color="text.disabled"
									sx={{ mt: 0.5 }}
								>
									<Translate String="Training.Drill.KpiParameters.NoParameters.Hint">
										Add KPI parameters when editing this drill.
									</Translate>
								</Typography>
							</Paper>
						)}
					</Box>
				</Box>
			</Box>
		</Box>
	);
};
